import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  Hint,
  Inscription,
  List,
  OverflowText,
  RoundCloseIcon,
  useFormContext,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';

import ServiceCategoryIcon from '@/modules/employee/EnrollmentDetails/EnrollmentDetailsAccountInfo/EligibleServices/ServiceCategoryIcon';
import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import { CATEGORY_LABELS } from '@/modules/ProcessingDefinition/Services/Services.constants';
import { CategoryTypeDto } from '@/modules/ProcessingDefinition/Services/Services.types';
import colors from '@/styles/colors';

import {
  PayoutDefinitionFormValues,
  PayoutOrderPlan,
  PayoutOrderService,
} from '../../../PayoutDefinitionSetup.types';
import { getServiceColumnErrorKey } from '../hooks/usePayoutOrderValidation';
import { AnimatedCategoryName, AnimatedColumn } from '../PayoutOrder.styles';

import { CATEGORY_NAME_WIDTH_MAP, COLUMN_WIDTH_MAP } from './PayoutOrder.constants';
import { CategoryCardWrapper } from './PayoutOrderCategoryCard.styles';
import PayoutOrderPlanDropdown from './PayoutOrderPlanDropdown';

const MIXED_PLANS_LABEL = 'Mixed plans';

const getColumnState = (
  services: PayoutOrderService[],
  columnIndex: number
): { plan: PayoutOrderPlan | null; isMixed: boolean } => {
  if (!services.length) return { plan: null, isMixed: false };

  const plansInColumn = services.map((service) => service.plans.find((p) => p.order === columnIndex) || null);
  const nonNullPlans = plansInColumn.filter(Boolean) as PayoutOrderPlan[];

  if (nonNullPlans.length === 0) return { plan: null, isMixed: false };

  // Check if all plans are the same
  const firstId = nonNullPlans[0].id;
  const allSame = nonNullPlans.every((p) => p.id === firstId);

  if (!allSame) return { plan: null, isMixed: true };

  // All plans agree — show that plan
  return { plan: nonNullPlans[0], isMixed: false };
};

interface PayoutOrderCategoryCardProps {
  categoryType: CategoryTypeDto;
  serviceCount: number;
  services: PayoutOrderService[];
  isExpanded: boolean;
  isEditMode: boolean;
  columns: number[];
  onToggleExpand: () => void;
  onRemove: () => void;
  onUpdateCategoryPlan: (order: number, plan: PayoutOrderPlan | null) => void;
  columnsCount: number;
  showValidationErrors?: boolean;
  children?: React.ReactNode;
}

const PayoutOrderCategoryCard: React.FC<PayoutOrderCategoryCardProps> = ({
  categoryType,
  serviceCount,
  services,
  isExpanded,
  isEditMode,
  columns,
  onToggleExpand,
  onRemove,
  onUpdateCategoryPlan,
  columnsCount,
  showValidationErrors,
  children,
}) => {
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);

  /** Derive per-column state from actual service plans */
  const columnStates = useMemo(
    () => new Map(columns.map((col) => [col, getColumnState(services, col)])),
    [columns, services]
  );

  const { formState } = useFormContext<PayoutDefinitionFormValues>();

  /** Show error icon only when formState.errors has entries for this category's services (same source as dropdowns) */
  const hasIncompleteColumns = useMemo(() => {
    const errors = formState.errors as Record<string, { message?: string }>;
    return services.some((service) =>
      columns.some((col) => !!errors[getServiceColumnErrorKey(service.rowId, col)]?.message)
    );
  }, [formState.errors, services, columns]);

  const handleConfirmRemove = useCallback(() => {
    onRemove();
    setIsRemoveModalVisible(false);
  }, [onRemove]);

  const handleCategoryPlanChange = useCallback(
    (order: number, plan: PayoutOrderPlan | null) => {
      onUpdateCategoryPlan(order, plan);
    },
    [onUpdateCategoryPlan]
  );

  return (
    <>
      <CategoryCardWrapper
        $isExpanded={isExpanded}
        data-testid={`payout-order_category_${categoryType}_wrapper`}
        background="canvas"
        round="container2Round"
      >
        <Box
          data-testid={`payout-order_category_${categoryType}_header`}
          direction="row"
          align="center"
          pad={{ vertical: 'spacing12', horizontal: 'spacing24' }}
          height={{ min: '64px' }}
          style={{ cursor: 'pointer' }}
          onClick={onToggleExpand}
        >
          <Box direction="row" align="center" style={{ flex: 1 }} gap="s">
            <AnimatedCategoryName
              direction="row"
              align="center"
              gap="spacing12"
              style={{ width: CATEGORY_NAME_WIDTH_MAP[columnsCount] }}
            >
              {isExpanded ? <ChevronUpIcon color="textTitle" /> : <ChevronDownIcon color="textTitle" />}
              <ServiceCategoryIcon type={categoryType} color={colors.activity[categoryType]} size="24px" />
              <Box width={{ max: '180px' }}>
                <OverflowText
                  data-testid={`payout-order_category_${categoryType}_title`}
                  title={CATEGORY_LABELS[categoryType] || categoryType}
                  textStyle={{ fontWeight: 'bold' }}
                />
              </Box>
            </AnimatedCategoryName>

            {columns.map((columnIndex) => {
              const state = columnStates.get(columnIndex) ?? { plan: null, isMixed: false };
              return (
                <AnimatedColumn
                  key={columnIndex}
                  style={{ width: COLUMN_WIDTH_MAP[columnsCount] }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isEditMode && (
                    <PayoutOrderPlanDropdown
                      testId={`payout-order_category_${categoryType}_column_${columnIndex}`}
                      value={state.plan}
                      onChange={(plan: PayoutOrderPlan | null) => handleCategoryPlanChange(columnIndex, plan)}
                      columnsCount={columnsCount}
                      showWarningBanner
                      placeholder={state.isMixed ? MIXED_PLANS_LABEL : undefined}
                    />
                  )}
                  {!isEditMode && state.plan && (
                    <Box direction="row" align="center" gap="spacing8">
                      {state.plan.accountType && (
                        <PlanTypeIcon
                          type={state.plan.accountType as CardTypesEnum}
                          size="20px"
                          color={colors.cardTypes[state.plan.accountType as keyof typeof colors.cardTypes]}
                        />
                      )}
                      <OverflowText title={state.plan.name} textStyle={{ fontWeight: '400' }} />
                    </Box>
                  )}
                  {!isEditMode && state.isMixed && (
                    <Inscription size="14px" color="textBody" lineHeight="18px">
                      {MIXED_PLANS_LABEL}
                    </Inscription>
                  )}
                </AnimatedColumn>
              );
            })}
          </Box>

          <Box
            direction="row"
            align="center"
            style={{ flexShrink: 0, marginLeft: '12px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {showValidationErrors && hasIncompleteColumns && (
              <Box margin={{ right: 'spacing8' }}>
                <Hint
                  hintWidth={220}
                  hintElement={
                    <Box data-testid={`payout-order_category_${categoryType}_error-icon`}>
                      <RoundCloseIcon size="20px" color="danger" />
                    </Box>
                  }
                >
                  <Inscription size="small" color="textOnColor" textAlign="center" weight={500}>
                    Please review and correct the highlighted fields
                  </Inscription>
                </Hint>
              </Box>
            )}
            {isEditMode && (
              <List.Item.Options testId={`payout-order_category_${categoryType}_options`}>
                <List.Item.Options.Item
                  testId={`payout-order_category_${categoryType}_remove`}
                  onClick={() => setIsRemoveModalVisible(true)}
                  isDanger
                >
                  Remove
                </List.Item.Options.Item>
              </List.Item.Options>
            )}
          </Box>
        </Box>

        {isExpanded && (
          <Box
            data-testid={`payout-order_category_${categoryType}_content`}
            pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}
          >
            <Box border={{ side: 'top', color: 'border2', size: 'small' }} pad={{ top: 'spacing12' }}>
              <Inscription weight="bold" color="textBody" margin={{ bottom: 'spacing8' }}>
                Services: {serviceCount}
              </Inscription>
              <Box background="module" round="container2Round" pad="spacing8">
                {children}
              </Box>
            </Box>
          </Box>
        )}
      </CategoryCardWrapper>

      <WarnModal
        testId={`payout-order_category_${categoryType}_remove-modal`}
        iconNode={<WarningIcon size="48px" />}
        visible={isRemoveModalVisible}
        header="Remove Category?"
        onSetVisible={setIsRemoveModalVisible}
        cancelButtonNode={
          <Box direction="row" gap="spacing12">
            <AppButton
              testId={`payout-order_category_${categoryType}_remove-cancel`}
              buttonType="secondary"
              onClick={() => setIsRemoveModalVisible(false)}
              width="150px"
            >
              Cancel
            </AppButton>
            <AppButton
              testId={`payout-order_category_${categoryType}_remove-confirm`}
              color="red"
              onClick={handleConfirmRemove}
              width="150px"
            >
              Yes, Remove
            </AppButton>
          </Box>
        }
      />
    </>
  );
};

export default React.memo(PayoutOrderCategoryCard);
